import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, X, CornerDownLeft, Boxes, Package, Wrench, ShoppingCart, User, Truck, FileText, Ship, Factory, Wallet, Car, HandCoins, Contact, Warehouse, LayoutGrid,
} from 'lucide-react';
import { searchAPI } from '../../utils/api';

/**
 * v4.1.2 global search: the box in the top bar (Ctrl+K / ⌘K), a full-screen sheet on phones.
 * Pages of the menu are matched here; records come from /search (only sections the user may open).
 * Choosing a record opens its page with ?q=… so that page's own search box shows it.
 */
const ICON = {
  pages: LayoutGrid, raw_materials: Boxes, finished_goods: Package, misc_goods: Wrench, sales: ShoppingCart, customers: User, suppliers: Truck,
  purchases: Truck, lading_bills: FileText, shipments: Ship, production: Factory, expenses: Wallet, vehicles: Car, prepayments: HandCoins,
  employees: Contact, warehouses: Warehouse,
};
const norm = s => (s || '').toLowerCase().replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/‌/g, '')
  .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

export default function GlobalSearch({ pages, mode = 'desktop' }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dir = lang === 'fa' ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);          // desktop dropdown
  const [sheet, setSheet] = useState(false);        // phone full-screen
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(0);
  const box = useRef();
  const inp = useRef();
  const sheetInp = useRef();
  const seq = useRef(0);

  // Ctrl+K / ⌘K anywhere
  useEffect(() => {
    const h = e => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.code === 'KeyK')) {
        const narrow = window.matchMedia('(max-width: 768px)').matches;
        if (mode === 'mobile' && narrow) { e.preventDefault(); setSheet(true); }
        if (mode === 'desktop' && !narrow) { e.preventDefault(); inp.current?.focus(); inp.current?.select(); setOpen(true); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [mode]);
  useEffect(() => {   // click outside closes the dropdown
    const h = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => { if (sheet) setTimeout(() => sheetInp.current?.focus(), 50); }, [sheet]);

  // ask the server (debounced; an older answer never overwrites a newer one)
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setRes(null); setBusy(false); return undefined; }
    setBusy(true);
    const my = ++seq.current;
    const h = setTimeout(() => {
      searchAPI.search(text).then(r => { if (my === seq.current) { setRes(r.data.groups); setBusy(false); } })
        .catch(() => { if (my === seq.current) { setRes([]); setBusy(false); } });
    }, 220);
    return () => clearTimeout(h);
  }, [q]);

  const pageHits = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    return pages.filter(p => words.every(w => norm(p.label).includes(w))).slice(0, 5)
      .map(p => ({ id: 'page' + p.path, title: p.label, route: p.path, page: true, Icon: p.icon }));
  }, [q, pages]);
  const groups = useMemo(() => [...(pageHits.length ? [{ key: 'pages', items: pageHits }] : []), ...(res || [])], [pageHits, res]);
  const flat = useMemo(() => groups.flatMap(g => g.items.map(it => ({ ...it, group: g.key }))), [groups]);
  useEffect(() => { setSel(0); }, [q, res]);

  const go = it => {
    if (!it) return;
    navigate(it.page ? it.route : `${it.route}?q=${encodeURIComponent(it.q || it.title || '')}`);
    setOpen(false); setSheet(false); setQ('');
    inp.current?.blur();
  };
  const onKey = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[sel]); }
    else if (e.key === 'Escape') { setOpen(false); setSheet(false); e.currentTarget.blur(); }
  };

  const results = (
    <div className="gs-results">
      {q.trim().length < 2 ? <div className="gs-hint">{t('gs_hint')}</div>
        : !flat.length ? <div className="gs-hint">{busy ? t('gs_searching') : t('gs_nothing')}</div>
        : groups.map(g => {
          const GI = ICON[g.key] || Search;
          return (
            <div key={g.key} className="gs-group">
              <div className="gs-gtitle"><GI size={12} /> {t('gs_g_' + g.key)}</div>
              {g.items.map(it => {
                const idx = flat.findIndex(f => f.id === it.id && f.group === g.key);
                const I = it.Icon || GI;
                return (
                  <button key={it.id} type="button" className={`gs-item ${idx === sel ? 'on' : ''}`} onMouseEnter={() => setSel(idx)}
                    onMouseDown={e => e.preventDefault()} onClick={() => go(flat[idx])}>
                    <I size={15} />
                    <span className="gs-txt"><b dir="auto">{it.title}</b>{it.sub && <small dir="auto">{it.sub}</small>}</span>
                    {idx === sel && <CornerDownLeft size={13} className="gs-enter" />}
                  </button>
                );
              })}
            </div>
          );
        })}
      {busy && flat.length > 0 && <div className="gs-busy" />}
    </div>
  );

  if (mode === 'mobile') {
    return (
      <>
        <button type="button" className="gs-mobile-btn" onClick={() => setSheet(true)} aria-label={t('gs_placeholder')}><Search size={18} /></button>
        {sheet && createPortal(
          <div className="gs-sheet" dir={dir}>
            <div className="gs-sheet-head">
              <div className="gs-box focus"><Search size={15} className="gs-ico" />
                <input ref={sheetInp} value={q} placeholder={t('gs_placeholder')} onChange={e => setQ(e.target.value)} onKeyDown={onKey} /></div>
              <button type="button" className="gs-close" onClick={() => { setSheet(false); setQ(''); }}>{t('cancel')}</button>
            </div>
            {results}
          </div>, document.body)}
      </>
    );
  }
  return (
    <>
      <div ref={box} className="gs desktop-sidebar" dir={dir}>
        <div className={`gs-box ${open ? 'focus' : ''}`}>
          <Search size={13} className="gs-ico" />
          <input ref={inp} value={q} placeholder={t('gs_placeholder')} onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} onKeyDown={onKey} aria-label={t('gs_placeholder')} />
          {q ? <button type="button" className="gs-clear" onClick={() => { setQ(''); inp.current?.focus(); }}><X size={13} /></button>
            : <kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>}
        </div>
        {open && <div className="gs-drop">{results}</div>}
      </div>
    </>
  );
}
